// src/services/adminService.js
// ✅ Même correction : pas de "/api" en dur, base unique + helper apiFetch
// CRA:  REACT_APP_API_BASE_URL=http://localhost:4000
// Vite: VITE_API_BASE_URL=http://localhost:4000

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  "https://nouzhaback-theta.vercel.app";

// Ton backend est du style: http://localhost:4000/api/...
const API_PREFIX = "/api";

/* ---------------------------
   Helpers
--------------------------- */
const getToken = () => localStorage.getItem("authToken");

const buildHeaders = ({ json = true } = {}) => {
  const token = getToken();
  const h = {};
  if (json) h["Content-Type"] = "application/json";
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

// fetch unifié : gère JSON / FormData + erreurs
const apiFetch = async (path, { method = "GET", body, headers, json = true } = {}) => {
  const url = `${API_BASE}${API_PREFIX}${path}`;

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const finalHeaders = {
    ...buildHeaders({ json: json && !isFormData }),
    ...(headers || {}),
  };

  const res = await fetch(url, {
    method,
    headers: finalHeaders,
    body: body ? (isFormData ? body : json ? JSON.stringify(body) : body) : undefined,
  });

  // certains DELETE peuvent renvoyer 204
  if (res.status === 204) return { success: true };

  let data;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg =
      data?.message ||
      data?.error ||
      `Erreur HTTP ${res.status}`;
    throw new Error(msg);
  }

  return data;
};

/* ---------------------------
   Admin Service
--------------------------- */
const adminService = {
  // Dashboard
  getDashboardStats: () => apiFetch("/admin/dashboard/stats"),
  getRecentActivity: () => apiFetch("/admin/dashboard/activity"),

  // Paiements (Orders CMI)
  getPayments: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ).toString();
    return apiFetch(`/admin/payments${qs ? `?${qs}` : ""}`);
  },
  getPaymentDetail: (id) => apiFetch(`/admin/payments/${id}`),
  refundPayment: (id, body) => apiFetch(`/admin/payments/${id}/refund`, { method: "POST", body }),

  // Paramètres (modes de paiement)
  getSettings: () => apiFetch("/admin/settings"),
  updateSettings: (body) => apiFetch("/admin/settings", { method: "PUT", body }),

  // Auth
  changePassword: (passwordData) =>
    apiFetch("/admin/change-password", { method: "POST", body: passwordData }),

  // Profile image (FormData)
  updateProfileImage: (formData) =>
    apiFetch("/admin/profile-image", { method: "POST", body: formData }),

  // Models
  getAvailableModels: () => apiFetch("/admin/models"),
  getModelSchema: (modelName) => apiFetch(`/admin/models/${encodeURIComponent(modelName)}/schema`),

  // CRUD générique
  getAllItems: (modelName, { page = 1, limit = 20, search = "" } = {}) => {
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      search,
    }).toString();
    return apiFetch(`/admin/${encodeURIComponent(modelName)}?${qs}`);
  },

  getItemById: (modelName, id) =>
    apiFetch(`/admin/${encodeURIComponent(modelName)}/${encodeURIComponent(id)}`),

  createItem: (modelName, data) =>
    apiFetch(`/admin/${encodeURIComponent(modelName)}`, {
      method: "POST",
      body: data, // JSON ou FormData (auto-détecté)
    }),

  updateItem: (modelName, id, data) =>
    apiFetch(`/admin/${encodeURIComponent(modelName)}/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: data, // JSON ou FormData
    }),

  deleteItem: (modelName, id) =>
    apiFetch(`/admin/${encodeURIComponent(modelName)}/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // Factures
  getInvoiceRequests: () => apiFetch("/invoice-requests/admin/list"),

  updateInvoiceRequestStatus: (requestId, status, notes) =>
    apiFetch(`/invoice-requests/admin/${encodeURIComponent(requestId)}`, {
      method: "PUT",
      body: { status, notes },
    }),

  sendInvoiceEmailWithPDF: (requestId, subject, message, pdfBuffer, pdfFilename) =>
    apiFetch("/invoice-requests/admin/send-invoice-email", {
      method: "POST",
      body: { requestId, subject, message, pdfBuffer, pdfFilename },
    }),

  // --------------------
  // Réservations hôtels
  // --------------------
  assignHotelToReservation: (reservationId, hotelId) =>
    apiFetch(`/admin/reservationHotels/${encodeURIComponent(reservationId)}/assign-hotel`, {
      method: "PUT",
      body: { hotelId },
    }),

  updateHotelReservationPrice: (reservationId, prixFinal) =>
    apiFetch(`/admin/reservationHotels/${encodeURIComponent(reservationId)}/price`, {
      method: "PUT",
      body: { prixFinal },
    }),

  generateHotelReservationPaymentLink: (reservationId, prixFinal) =>
    apiFetch(`/admin/reservationHotels/${encodeURIComponent(reservationId)}/payment-link`, {
      method: "POST",
      body: { prixFinal },
    }),

  sendHotelReservationPaymentEmail: (reservationId, subject, messageTemplate) =>
    apiFetch(`/admin/reservationHotels/${encodeURIComponent(reservationId)}/send-payment-email`, {
      method: "POST",
      body: { subject, messageTemplate },
    }),

  // Matching hotels
  getMatchingHotels: (filters = {}) => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") qs.append(k, String(v));
    });
    return apiFetch(`/admin/hotels/match?${qs.toString()}`);
  },

  // --------------------
  // Circuits
  // --------------------
  // ⚠️ Si cette route est publique chez toi, tu peux faire une version "publicFetch" sans Authorization.
  getCircuitById: (id) =>
    apiFetch(`/circuits/${encodeURIComponent(id)}`),

  getCircuitsByDestination: (destination) => {
    const qs = new URLSearchParams();
    if (destination) qs.append("destination", destination);
    return apiFetch(`/admin/circuits/match?${qs.toString()}`);
  },

  assignCircuitToReservation: (reservationId, circuitId) =>
    apiFetch(`/admin/reservationCircuits/${encodeURIComponent(reservationId)}/assign-circuit`, {
      method: "PUT",
      body: { circuitId },
    }),

  updateCircuitReservationPrice: (reservationId, prixFinal) =>
    apiFetch(`/admin/reservationCircuits/${encodeURIComponent(reservationId)}/price`, {
      method: "PUT",
      body: { prixFinal },
    }),

  generateCircuitReservationPaymentLink: (reservationId, prixFinal) =>
    apiFetch(`/admin/reservationCircuits/${encodeURIComponent(reservationId)}/payment-link`, {
      method: "POST",
      body: { prixFinal },
    }),

  sendCircuitReservationPaymentEmail: (reservationId, subject, messageTemplate) =>
    apiFetch(`/admin/reservationCircuits/${encodeURIComponent(reservationId)}/send-payment-email`, {
      method: "POST",
      body: { subject, messageTemplate },
    }),

  // --------------------
  // Transports
  // --------------------
  assignTransportToReservation: (reservationId, transportId) =>
    apiFetch(`/admin/reservationTransports/${encodeURIComponent(reservationId)}/assign-transport`, {
      method: "PUT",
      body: { transportId },
    }),

  updateTransportReservationPrice: (reservationId, prixFinal) =>
    apiFetch(`/admin/reservationTransports/${encodeURIComponent(reservationId)}/price`, {
      method: "PUT",
      body: { prixFinal },
    }),

  generateTransportReservationPaymentLink: (reservationId) =>
    apiFetch(`/admin/reservationTransports/${encodeURIComponent(reservationId)}/payment-link`, {
      method: "POST",
      body: {},
    }),

  sendTransportReservationPaymentEmail: (reservationId, subject, messageTemplate) =>
    apiFetch(`/admin/reservationTransports/${encodeURIComponent(reservationId)}/send-payment-email`, {
      method: "POST",
      body: { subject, messageTemplate },
    }),

  // --------------------
  // Voitures
  // --------------------
  assignVoitureToReservation: (reservationId, voitureId) =>
    apiFetch(`/admin/reservationVoitures/${encodeURIComponent(reservationId)}/assign-voiture`, {
      method: "PUT",
      body: { voitureId },
    }),

  updateVoitureReservationPrice: (reservationId, prixFinal) =>
    apiFetch(`/admin/reservationVoitures/${encodeURIComponent(reservationId)}/price`, {
      method: "PUT",
      body: { prixFinal },
    }),

  generateVoitureReservationPaymentLink: (reservationId) =>
    apiFetch(`/admin/reservationVoitures/${encodeURIComponent(reservationId)}/payment-link`, {
      method: "POST",
      body: {},
    }),

  sendVoitureReservationPaymentEmail: (reservationId, payload) =>
    apiFetch(`/admin/reservationVoitures/${encodeURIComponent(reservationId)}/send-payment-email`, {
      method: "POST",
      body: payload || {},
    }),

  // --------------------
  // Visites
  // --------------------
  assignVisiteToReservation: (reservationId, visiteId) =>
    apiFetch(`/admin/reservationVisites/${encodeURIComponent(reservationId)}/assign-visite`, {
      method: "PUT",
      body: { visiteId },
    }),

  updateVisiteReservationPrice: (reservationId, prixFinal) =>
    apiFetch(`/admin/reservationVisites/${encodeURIComponent(reservationId)}/price`, {
      method: "PUT",
      body: { prixFinal },
    }),

  generateVisiteReservationPaymentLink: (reservationId) =>
    apiFetch(`/admin/reservationVisites/${encodeURIComponent(reservationId)}/payment-link`, {
      method: "POST",
      body: {},
    }),

  sendVisiteReservationPaymentEmail: (reservationId, payload) =>
    apiFetch(`/admin/reservationVisites/${encodeURIComponent(reservationId)}/send-payment-email`, {
      method: "POST",
      body: payload || {},
    }),

  getVisitesByVille: (ville) => {
    const v = (ville || "").trim();
    if (!v) throw new Error("Ville requise");
    return apiFetch(`/visites/ville/${encodeURIComponent(v)}`);
  },
};

export default adminService;
